<template>
    <div>
        <div class="d-flex justify-end pr-3">

            <v-tooltip top color="primary">
                <template v-slot:activator="{ on }">
                    <v-btn :disabled="!hasPermission('invite users')"
                           v-on="on"
                           icon
                           class="mr-2 elevation-0"
                           @click="openPending()">
                        <v-icon>mdi-clock-outline</v-icon>
                    </v-btn>
                </template>
                <span>View pending invitations</span>
            </v-tooltip>

            <v-tooltip top color="primary">
                <template v-slot:activator="{ on }">
                    <v-btn class="mr-2 elevation-0"
                           :disabled="!hasPermission('view users')"
                           @click="getUsers(true)"
                           v-on="on"
                           icon>
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </template>
                <span>Reload data</span>
            </v-tooltip>

            <v-tooltip top color="primary">
                <template v-slot:activator="{ on }">
                    <v-btn v-if="hasPermission('invite users')"
                           v-on="on"
                           fab
                           small
                           class="mr-2 elevation-0"
                           color="primary"
                           @click="showInviteDialog=!showInviteDialog">
                        <v-icon>mdi-account-plus</v-icon>
                    </v-btn>
                </template>
                <span>Invite new user</span>
            </v-tooltip>

        </div>

        <v-data-table v-if="hasPermission('view users')"
                      v-bind:headers="headers"
                      :items="users"
                      class="elevation-1"
                      :loading="usersLoading || deleting"
                      :options.sync="tableOptions"
                      :footer-props="{itemsPerPageOptions: [10,50, -1]}"
        >
            <template v-slot:body="{ items }">
                <tbody>
                <tr v-for="item in items" :key="item.id">
                    <td class="text-left">
                        <avatar :image-url="item.photo_url" :person-name="item.name">
                            <span>
                                <b>{{ item.name }}</b><br>
                                Account was created {{ toHumanTill(item.created_at) }}
                            </span>
                        </avatar>
                    </td>
                    <td class="text-left text-no-wrap">{{ item.name }}</td>
                    <td class="text-left text-no-wrap">{{ item.email }}</td>

                    <!-- ROLES -->
                    <td class="text-left">
                        <v-tooltip color="primary" top>
                            <template v-slot:activator="{ on }">
                                <v-btn
                                    class="ma-0 elevation-0"
                                    fab
                                    x-small
                                    :color="hasPermission('view roles') ? 'primary' : 'grey'"
                                    v-on="on"
                                    @click.stop="openRoleEditor(item)">
                                    <v-icon light small color="white">mdi-security</v-icon>
                                </v-btn>
                            </template>
                            View roles
                        </v-tooltip>
                    </td>

                    <!-- LAST ONLINE -->
                    <td>
                        <div v-if="item.last_online_at">
                            {{ toHumanFrom(item.last_online_at) }}
                        </div>
                        <div v-else>
                            Never
                        </div>
                    </td>
                    <!-- LAST LOGIN -->
                    <td>
                        <div v-if="item.last_login_at">
                            {{ toHumanFrom(item.last_login_at) }}
                        </div>
                        <div v-else>
                            Never
                        </div>
                    </td>

                    <td class="text-left">
                        <div class="text-no-wrap">

                            <v-tooltip top color="primary">
                                <template v-slot:activator="{ on }">
                                    <v-btn :disabled="!hasPermission('update users')"
                                           class="ma-0 elevation-0 mr-2"
                                           icon
                                           x-small
                                           v-on="on"
                                           @click.stop="editUserClicked(item)">

                                        <v-icon light small color="white">mdi-pencil</v-icon>
                                    </v-btn>
                                </template>
                                <span>Edit user</span>
                            </v-tooltip>

                            <v-tooltip top color="primary">
                                <template v-slot:activator="{ on }">
                                    <v-btn :disabled="!hasPermission('delete users') || currentUser === item.id"
                                           class="ma-0 elevation-0"
                                           icon
                                           x-small
                                           v-on="on"
                                           @click.stop="deleteUserClicked(item)">

                                        <v-icon light small color="white">mdi-delete</v-icon>
                                    </v-btn>
                                </template>
                                <span>Delete user</span>
                            </v-tooltip>
                        </div>
                    </td>
                </tr>
                </tbody>
            </template>
        </v-data-table>

        <!-- Dialogs -->
        <assign-user-role-dialog
            v-model="userRoleDialogModel"
            :roles="selectedUserRoles"
            :available-roles="roles"
            @roles-changed="rolesChanged"
        />

        <edit-user-dialog
                v-if="hasPermission('update users')"
                v-model="userDialogModel"
                :user="userUnderEdit"
        ></edit-user-dialog>

        <invitation-dialog
            v-if="hasPermission('invite users')"
            :roles="roles"
            :show-dialog="showInviteDialog"
            :error-messages="inviteErrors"
            :loading="inviteUserLoading"
            @close-dialog="showInviteDialog = false"
            @user-invited="userInvited"
        />

        <pending-invitations-dialog
            v-if="hasPermission('invite users')"
            :invitations="pendingInvitations"
            :loading="pendingInvitationsLoading"
            :show-dialog="showPendingDialog"
            @cancel-invitation="cancelInvitation"
            @close-dialog="showPendingDialog = false"
        />

        <delete-confirmation-dialog v-if="userUnderDelete"
                                    v-model="showConfirmationDialog"
                                    delete
                                    :users="users"
                                    :user="userUnderDelete"
                                    @data="doUserDelete"
        ></delete-confirmation-dialog>

    </div>
</template>

<script>

import Json from 'vue-json-pretty';
import {mapState, mapActions, mapGetters} from 'vuex';
import {timeDisplay} from "../../../../components/mixins/timeDisplayMixin";
import Avatar from '../../../../components/UI/Avatar';
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import AssignUserRoleDialog from "./AssignUserRolesDialog";
import InvitationDialog from "./InvitationDialog";
import PendingInvitationsDialog from "./PendingInvitationsDialog";
import EditUserDialog from "./EditUserDialog";

const vuexStore = 'UserManager';

export default {

    mixins: [timeDisplay],

    components: {
        InvitationDialog,
        AssignUserRoleDialog,
        EditUserDialog,
        Json,
        Avatar,
        DeleteConfirmationDialog,
        PendingInvitationsDialog
    },

    data() {
        return {
            userRoleDialogModel: false,
            showPendingDialog: false,
            showInviteDialog: false,
            showConfirmationDialog: false,
            userUnderDelete: null,
            deleting: false,
            roleEditorUser: null,

            userDialogModel: false,
            userUnderEdit: null,
            showUserEditorDialog: false,

            tableOptions: {
                sortBy: ['name'],
                sortDesc: [false],
            },

            inviteErrors: {},
            inviteUserLoading: false,

            headers: [
                {
                    align: 'left',
                    text: '',
                    sortable: false,
                    value: 'name',
                    width: '1px'
                },
                {
                    text: 'NAME',
                    align: 'left',
                    sortable: true,
                    value: 'name'
                },
                {
                    text: 'EMAIL',
                    align: 'left',
                    sortable: false,
                    value: 'email',
                    width: '1px'
                },
                {
                    text: 'ROLES',
                    align: 'left',
                    sortable: false,
                    value: 'name',
                },
                {
                    text: 'LAST ONLINE',
                    align: 'left',
                    sortable: false,
                    value: 'last_online_at'
                },
                {
                    text: 'LAST LOGIN',
                    align: 'left',
                    sortable: false,
                    value: 'name',
                },
                {
                    text: 'ACTIONS',
                    align: 'left',
                    sortable: false,
                    value: 'name',
                    width: '80px'
                },
            ],
        }
    },

    watch: {
        usersLoadError: function (error) {
            if (error) Bus.$emit('flash-message', {type: 'error', message: 'Server is not responding...'});
        },
    },

    computed: {

        ...mapState(vuexStore, [
            'users',
            'roles',
            'usersLoading',
            'usersLoadError',
            'permissions',
            'pendingInvitations',
            'pendingInvitationsLoading'
        ]),

        ...mapGetters(vuexStore, ['users']),

        currentUser() {
            return this.$store.state.user.id;
        },

        selectedUserRoles() {
            if (this.roleEditorUser) {
                return [...this.roleEditorUser.roles];
            }
            return [];
        },

        confirmationDescription() {

            if (this.$store.state.user && this.userUnderDelete) {

                if (this.$store.state.user.id === this.userUnderDelete.id) {
                    return 'Now you are been silly! Are you sure you want to delete yourself?';
                } else {
                    return 'Are you sure want to delete ' + this.userUnderDelete.name + '?';
                }
            }
        },
    },

    methods: {

        ...mapActions('UserManager', [
            'getUsers',
            'saveRoles',
            'loadUserRoles',
            'saveUserRoles',
            'getPendingInvitations',
            'inviteUser',
            'cancelPendingInvitation',
            'deleteUser'
        ]),

        // Global actions
        ...mapActions(['loadUser']),

        hasAdminRole(user) {
            let found = user.roles.find(role => role.name === 'Admin')
            return !!found;
        },

        rolesChanged(data) {

            this.saveUserRoles({userId: this.roleEditorUser.id, roles: data})
                .catch(error => {
                    if (error.response.status === 403) {

                        Bus.$emit('flash-message', {
                            'type': 'error',
                            'message': "You are not authorized modify this user's roles!"
                        });

                    } else if (error.response.status === 406) {

                        Bus.$emit('flash-message', {
                            'type': 'error',
                            'message': "You cannot remove Admin role from yourself. " +
                                "You need ask an another Admin to do this for you."
                        });
                    } else {
                        Bus.$emit('server-error');
                    }
                });

        },

        userInvited(data) {
            this.inviteUserLoading = true;

            this.inviteUser(data)
                .then(() => {

                    Bus.$emit('flash-message', {
                        'type': 'info',
                        'message': 'User ' + data.name + ' has been invited!'
                    });

                    this.showInviteDialog = false;
                    this.inviteUserLoading = false;
                })
                .catch(error => {

                    this.inviteUserLoading = false;

                    if (error.status === 403) {

                        Bus.$emit('flash-message', {
                            'type': 'error',
                            'message': 'You are not authorized to invite users'
                        });
                        this.showInviteDialog = false;

                    } else if (error.status === 422) {
                        this.inviteErrors = error.data.errors;

                    } else {
                        // TODO: Could leak technical details
                        Bus.$emit('flash-message', {type: 'error', 'message': error.data.message});
                    }
                })

        },

        cancelInvitation(e) {
            this.cancelPendingInvitation(e)
                .catch((response) => {
                    if (response.status === 403) {

                        Bus.$emit('flash-message', {
                            'type': 'error',
                            'message': 'You are not authorized to cancel user invitations'
                        });
                    } else {
                        Bus.$emit('flash-message', {type: 'error', 'message': response.data.message});
                    }
                })
                .then(() => {
                });
        },


        deleteUserClicked(item) {
            this.userUnderDelete = item;
            this.showConfirmationDialog = true;
        },

        editUserClicked(user) {
            this.userUnderEdit = Object.assign({}, user);
            this.userDialogModel = true;
        },

        doUserDelete(data) {
            if (data.confirmed) {

                this.deleting = true;

                this.deleteUser({
                    id: this.userUnderDelete.id,
                    assignTo: data.assignTo.id,
                })
                    .then(() => {
                        Bus.$emit('flash-message', {type: 'info', 'message': 'User successfully deleted'});
                    })
                    .catch((error) => {
                        console.log(error);
                        Bus.$emit('flash-message', {type: 'error', 'message': error.response.data.message});
                    })
                    .finally(() => {
                        this.deleting = false;
                    })
            }
        },

        openPending() {

            // Get all roles and permissions
            this.getPendingInvitations()
                .catch(() => {
                    Bus.$emit('server-error');
                })

            this.showPendingDialog = true;
        },

        openRoleEditor(user) {
            this.roleEditorUser = user;
            this.userRoleDialogModel = true;
        },
    }
}
</script>
